import { ReactElement } from 'react'
import type { SvgIconComponent } from '@mui/icons-material'
import { Box, Tooltip, useTheme } from '@mui/material'
import type { Theme } from '@mui/material/styles'
import { MaterialSymbol } from '@ui2/icons'
import { DEFAULT_SORT_MODES, type SortDir, type SortMode } from '../types'

const SORT_OPTIONS: {
  id: SortMode
  title: string
  render: (active: boolean, theme: Theme) => ReactElement
}[] = [
  {
    id: 'eta',
    title: 'η',
    render: (active, theme) => (
      <MaterialSymbol
        icon="trending_down"
        size={16}
        sx={{ color: active ? theme.palette.primary.main : theme.palette.text.secondary }}
      />
    ),
  },
  {
    id: 'etaC',
    title: 'η c,90',
    render: (active, theme) => (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.25,
          color: active ? theme.palette.primary.main : theme.palette.text.secondary,
          fontFamily: 'Roboto',
          fontSize: 12,
          fontWeight: 500,
        }}
      >
        <MaterialSymbol icon="trending_down" size={14} />
        <Box component="span" sx={{ fontSize: 11 }}>
          η
          <Box component="sub" sx={{ fontSize: 7 }}>
            c,90
          </Box>
        </Box>
      </Box>
    ),
  },
  {
    id: 'trailingMetric',
    // Replaced by the caller's `metricSortLabel`; the option is hidden without one.
    title: '',
    render: (active, theme) => (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.25,
          color: active ? theme.palette.primary.main : theme.palette.text.secondary,
          fontFamily: 'Roboto',
          fontSize: 12,
          fontWeight: 500,
        }}
      >
        <MaterialSymbol icon="trending_down" size={14} />
        <Box component="span" sx={{ fontSize: 11 }}>
          F
          <Box component="sub" sx={{ fontSize: 7 }}>
            d
          </Box>
        </Box>
      </Box>
    ),
  },
  {
    id: 'alpha',
    title: 'Alphabetisch',
    render: (active, theme) => (
      <MaterialSymbol
        icon="sort_by_alpha"
        size={16}
        sx={{ color: active ? theme.palette.primary.main : theme.palette.text.secondary }}
      />
    ),
  },
  {
    id: 'crossSection',
    title: 'Nach Querschnitt',
    render: (active, theme) => (
      <MaterialSymbol
        icon="crop_square"
        size={16}
        sx={{ color: active ? theme.palette.primary.main : theme.palette.text.secondary }}
      />
    ),
  },
  {
    id: 'anchor',
    title: 'Nach Anker',
    render: (active, theme) => (
      <MaterialSymbol
        icon="anchor"
        size={16}
        sx={{ color: active ? theme.palette.primary.main : theme.palette.text.secondary }}
      />
    ),
  },
  {
    id: 'tree',
    title: 'Baumansicht',
    render: (active, theme) => (
      <MaterialSymbol
        icon="account_tree"
        size={16}
        sx={{ color: active ? theme.palette.primary.main : theme.palette.text.secondary }}
      />
    ),
  },
]

export type SortGroupProps = {
  value: SortMode
  direction: SortDir
  onChange: (next: SortMode) => void
  groupingEnabled?: boolean
  /**
   * Tooltip of the trailing-metric sort option (anchors: the design tensile load). The option is
   * only offered for lists that name it, i.e. those whose rows carry a trailing metric.
   */
  metricSortLabel?: string
  /** Set false for lists whose rows have no η_c,90 (anchors), which cannot sort by it. */
  etaCSortEnabled?: boolean
  /**
   * Renames the `qs`-column sort option, whose meaning is list-specific: a cross section for
   * member lists, the chosen anchor for the anchors list.
   */
  qsSortOption?: { label: string; icon: SvgIconComponent }
  /** Which modes this list offers, in the order they are shown. */
  modes?: SortMode[]
}

export const SortGroup = ({
  value,
  direction,
  onChange,
  groupingEnabled = true,
  metricSortLabel,
  etaCSortEnabled = true,
  qsSortOption,
  modes = DEFAULT_SORT_MODES,
}: SortGroupProps): ReactElement => {
  const theme = useTheme()
  const offered = modes.filter(
    mode =>
      (groupingEnabled || mode !== 'tree') &&
      (metricSortLabel !== undefined || mode !== 'trailingMetric') &&
      (etaCSortEnabled || mode !== 'etaC'),
  )
  const optionById = new Map(SORT_OPTIONS.map(option => [option.id, option]))
  const options = offered
    .map(mode => optionById.get(mode))
    .filter((option): option is typeof SORT_OPTIONS[number] => option !== undefined)
    .map(option => {
      if (option.id === 'trailingMetric') return { ...option, title: metricSortLabel as string }
      if (option.id === 'crossSection' && qsSortOption) {
        const QsSortIcon = qsSortOption.icon
        return {
          ...option,
          title: qsSortOption.label,
          render: (active: boolean, optionTheme: Theme) => (
            <QsSortIcon
              sx={{
                fontSize: 16,
                color: active
                  ? optionTheme.palette.primary.main
                  : optionTheme.palette.text.secondary,
              }}
            />
          ),
        }
      }
      return option
    })
  return (
    <Box
      sx={{
        display: 'inline-flex',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        overflow: 'hidden',
        height: 28,
        flexShrink: 0,
      }}
    >
      {options.map((option, i) => {
        const active = value === option.id
        const directional = option.id !== 'tree'
        const title =
          active && directional
            ? `${option.title} (${direction === 'asc' ? 'aufsteigend' : 'absteigend'})`
            : option.title
        return (
          <Tooltip key={option.id} title={title} arrow disableInteractive>
            <Box
              component="button"
              type="button"
              onClick={() => onChange(option.id)}
              sx={{
                border: 0,
                px: 1,
                cursor: 'pointer',
                borderLeft: i === 0 ? 'none' : `1px solid ${theme.palette.divider}`,
                background: active ? 'rgba(55,132,52,0.10)' : theme.palette.common.white,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.25,
                minWidth: 30,
              }}
            >
              {option.render(active, theme)}
              {active && directional && (
                <MaterialSymbol
                  icon={direction === 'asc' ? 'arrow_upward' : 'arrow_downward'}
                  size={12}
                  sx={{ color: theme.palette.primary.main }}
                />
              )}
            </Box>
          </Tooltip>
        )
      })}
    </Box>
  )
}
